import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Dialog, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import CheckIcon from "@iconify/icons-mdi/check";
import CloseIcon from "@iconify/icons-mdi/close";
import { InlineIcon } from "@iconify/react";
import "moment/locale/hu";
import MyTimeRecordingPeriodChip from "my-time/components/recording-perid-chip/recording-perid-chip";
import {
  employeeAdmissionsApproval,
  employeeAdmissionsApprovalIsMobile,
  myTimeDashboardListTableDefIsMobile,
  removeColumns,
} from "my-time/configs/table-defs";
import { useMyTimeUserApproval } from "my-time/hooks/use-my-time-user-approval";
import {
  OcCard,
  OcCardButtonBar,
  OcDataTable,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcIsMobile,
} from "oc/hooks";

moment.locale("hu");

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface MyTimeUserApprovalDialogProps {
  list: { date: string;[key: string]: any }[];
  loading?: boolean;
  allTimesNull?: boolean;
  isExaminerMode?: boolean;
  open?: boolean;
  onClose?: () => void;
  selectedUser?: any;
  examinerTableDef?: any;
  canExaminerApprove?: boolean;
  onSelectRow?: (props: any) => void;
  mutationLoading?: boolean;
}

type ApprovalItem = MyTimeUserApprovalDialogProps["list"][number];

export const MyTimeUserApprovalDialog: React.FC<
  MyTimeUserApprovalDialogProps
> = ({ list, loading, allTimesNull, isExaminerMode = false, open: externalOpen, onClose: externalOnClose, selectedUser, examinerTableDef: examinerTableDefProp, canExaminerApprove = false, onSelectRow: onSelectRowProp, mutationLoading = false }) => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const [internalOpenDialog, setInternalOpenDialog] = useState(true);
  const {
    userApprovalEnabled,
    approvalStateLoading,
    isAcknowledged,
    createAcknowledgedByUser,
    createAcknowledgedByUserLoading,
  } = useMyTimeUserApproval();

  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(undefined);
  const [selectedUserRowId, setSelectedUserRowId] = useState<string | number | undefined>(undefined);

  let tableId = "employeeAdmissionsApprovelTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "title",
    pageSize: 5,
  });

  const examinerTableDef = useMemo(() => {
    if (!isExaminerMode || !examinerTableDefProp) return null;
    return removeColumns(examinerTableDefProp, ["viewWorkSchedule"]);
  }, [isExaminerMode, examinerTableDefProp]);

  const selectedUserTableDef = useMemo(() => {
    if (!isExaminerMode) return null;
    if (isMobile) {
      return myTimeDashboardListTableDefIsMobile;
    }
    return examinerTableDef;
  }, [isExaminerMode, isMobile, examinerTableDef]);

  const selectedUserFilteredTableDef = useMemo(() => {
    if (!isExaminerMode || !examinerTableDef) return null;
    return {
      id: examinerTableDef.id,
      columns: examinerTableDef.columns?.filter(
        (column: any) =>
          !myTimeDashboardListTableDefIsMobile?.columns?.some(
            (mobileColumn: any) => mobileColumn?.id === column?.id,
          ),
      ),
    };
  }, [isExaminerMode, examinerTableDef]);

  const openDialog = isExaminerMode ? (externalOpen ?? false) : internalOpenDialog;

  if (!isExaminerMode && (!userApprovalEnabled || isAcknowledged)) return null;

  const data = { rows: list };
  const handleClose = () => {
    if (isExaminerMode && externalOnClose) {
      externalOnClose();
    } else {
      setInternalOpenDialog(false);
    }
  };

  const handlePrimaryButton = () => {
    if (isExaminerMode && onSelectRowProp && selectedUser) {
      onSelectRowProp({ action: "authorize", row: selectedUser });
    } else {
      createAcknowledgedByUser();
    }
  };

  const tableDef = isMobile
    ? employeeAdmissionsApprovalIsMobile
    : employeeAdmissionsApproval;

  const filteredTableDef = {
    id: employeeAdmissionsApproval?.id,
    columns: employeeAdmissionsApproval?.columns?.filter(
      (column: any) =>
        !employeeAdmissionsApprovalIsMobile?.columns?.some(
          (mobileColumn: any) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const onSelectRow = (o: ApprovalItem) => {
    if (isMobile) {
      setSelectedRowId(prev =>
        prev === o._id
          ? undefined
          : o._id
      );
    }
  };

  const onSelectUserRow = (o: any) => {
    if (isMobile) {
      setSelectedUserRowId(prev =>
        prev === o._id
          ? undefined
          : o._id
      );
    } else if (onSelectRowProp) {
      onSelectRowProp(o);
    }
  };

  let primaryButtonText;
  let primaryButtonDisabled = loading;

  switch (isExaminerMode) {
    case false:
      primaryButtonText = t("MY_TIME_ACKNOWLEDGE");
      primaryButtonDisabled = loading || createAcknowledgedByUserLoading;
      break;
    case true:
      primaryButtonText = canExaminerApprove ? t("MY_TIME_AUTHORIZATION") : undefined;
      primaryButtonDisabled = loading || !selectedUser?.isApproved || selectedUser?.isAuthorized;
      break;
    default:
      primaryButtonText = undefined;
  }

  return (
    <Dialog
      open={openDialog && (isExaminerMode || !approvalStateLoading) && !allTimesNull}
      fullScreen
      slots={{ transition: Transition }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#ffffff' }}>
          <OcCard
            label={t("MY_TIME_WORKING_SCHEDULE_ACCEPT")}
            loading={loading || (!isExaminerMode && createAcknowledgedByUserLoading) || mutationLoading}
          >
            <Box position="absolute" right="32px" top="16px">
              <InlineIcon
                icon={CloseIcon}
                width="24px"
                onClick={handleClose}
                style={{ cursor: "pointer", color: "#4D4D4D" }}
              />
            </Box>
            {isExaminerMode && selectedUser && selectedUserTableDef && (
              <Box marginBottom="16px">
                <OcDataTable
                  tableDef={selectedUserTableDef}
                  filteredTableDef={selectedUserFilteredTableDef}
                  tableId="selectedUserTableData"
                  tableData={initialTableData}
                  setTableData={() => { }}
                  data={{ rows: [selectedUser], total: 1 }}
                  loading={loading || mutationLoading}
                  onSelectRow={onSelectUserRow}
                  selectedRowId={selectedUserRowId}
                  handleMoreButton={onSelectRowProp}
                  pagination={false}
                  localPaging={true}
                />
              </Box>
            )}
            <MyTimeRecordingPeriodChip />
          </OcCard>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', padding: "0px 16px" }}>
          <OcDataTable
            tableDef={tableDef}
            tableId={tableId}
            tableData={tableData}
            setTableData={setTableData}
            data={data}
            loading={loading}
            onSelectRow={onSelectRow}
            isNotExaminer={true}
            pagination={true}
            localPaging={false}
            onShowInlineEditForm={undefined}
            inlineEdit={false}
            inlineEditForm={undefined}
            selectedRowId={selectedRowId}
            handleMoreButton={() => { }}
            filteredTableDef={filteredTableDef}
            matchingMyTimeitems={[]}
            onRowSelection={() => { }}
            examinerDasboard={false}
            role={"user"}
            myDocuments={undefined}
            removeUpload={() => { }}
            onEditDocument={() => { }}
          />
        </Box>
        <Box sx={{ position: 'sticky', bottom: 0, bgcolor: '#ffffff' }}>
          <Box padding="0px 16px">
            <OcCardButtonBar
              handleLeftButton={() => { }}
              leftButtonColor={undefined}
              leftButtonText={undefined}
              leftButtonDisabled={false}
              handleRightButton={handleClose}
              rightButtonColor="secondary"
              rightButtonText={t("OC_CANCEL")}
              rightButtonVariant="text"
              rightButtonDisabled={loading}
              handlePrimaryButton={handlePrimaryButton}
              primaryButtonText={primaryButtonText}
              primaryButtonDisabled={primaryButtonDisabled}
              startIcon={<InlineIcon icon={CheckIcon} width="20px" />}
            />
          </Box>
          <Box height="16px" />
        </Box>
      </Box>
    </Dialog>
  );
};

export default MyTimeUserApprovalDialog;
